/**
 * Tap-to-Activate Utility
 *
 * Reusable pattern for elements that need a two-step interaction on touch:
 *   1. First tap/hover shows a preview state (overlay, message, etc.)
 *   2. Second tap triggers the actual action
 *   On desktop: hover shows preview, click triggers action immediately
 *
 * Usage:
 *   import { tapToActivate } from '../utils/tap-to-activate.js';
 *
 *   // Simple: add preview class on first tap, fire callback on second
 *   tapToActivate(element, {
 *       onActivate: () => openDirections(),
 *       previewClass: 'is-previewing',
 *   });
 *
 *   // With overlay element — shows/hides an overlay on preview
 *   tapToActivate(element, {
 *       onActivate: () => map.enable(),
 *       overlayContent: '<svg>...</svg> Click to activate map',
 *       overlayClass: 'map-activate-overlay',
 *       activeClass: 'map-active',
 *   });
 */

const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
let activePreviews = new Set();

// Global: tap outside any previewed element clears all previews
if (isTouch) {
    document.addEventListener('click', (e) => {
        activePreviews.forEach(entry => {
            if (!entry.element.contains(e.target)) {
                clearPreview(entry);
            }
        });
    }, true);
}

function clearPreview(entry) {
    if (entry.previewClass) entry.element.classList.remove(entry.previewClass);
    if (entry.overlay) entry.overlay.classList.remove('is-visible');
    entry.previewing = false;
    activePreviews.delete(entry);
    if (entry.onDeactivate) entry.onDeactivate();
}

/**
 * Apply tap-to-activate behaviour to an element.
 *
 * @param {HTMLElement} element - The element to make tap-to-activate
 * @param {Object} options
 * @param {Function} options.onActivate - Called on activation (second tap / click)
 * @param {Function} [options.onDeactivate] - Called when preview is dismissed
 * @param {string} [options.previewClass] - CSS class added during preview state
 * @param {string} [options.activeClass] - CSS class added when activated
 * @param {string} [options.overlayContent] - HTML content for an auto-created overlay
 * @param {string} [options.overlayClass] - CSS class for the overlay element
 * @returns {Object} Controller with activate(), deactivate(), destroy() methods
 */
export function tapToActivate(element, options = {}) {
    const {
        onActivate,
        onDeactivate,
        previewClass = 'is-previewing',
        activeClass,
        overlayContent,
        overlayClass = 'tap-activate-overlay',
    } = options;

    let overlay = null;
    let activated = false;

    const entry = {
        element,
        previewClass,
        overlay: null,
        previewing: false,
        onDeactivate,
    };

    // Create overlay if content provided
    if (overlayContent) {
        overlay = document.createElement('div');
        overlay.className = overlayClass;
        overlay.innerHTML = overlayContent;
        element.style.position = element.style.position || 'relative';
        element.appendChild(overlay);
        entry.overlay = overlay;

        // Overlay click triggers the activate flow
        overlay.addEventListener('click', (e) => {
            e.stopPropagation();
            activate();
        });
    }

    function activate() {
        activated = true;
        clearPreview(entry);
        if (activeClass) element.classList.add(activeClass);
        if (overlay) overlay.classList.remove('is-visible');
        if (onActivate) onActivate();
    }

    function deactivate() {
        activated = false;
        if (activeClass) element.classList.remove(activeClass);
        if (overlay) overlay.classList.add('is-visible');
        entry.previewing = false;
        if (onDeactivate) onDeactivate();
    }

    // For non-overlay mode (like transport cards): click handler
    if (!overlayContent) {
        element.addEventListener('click', (e) => {
            if (isTouch && !entry.previewing) {
                e.preventDefault();
                e.stopPropagation();
                // Clear any other previews
                activePreviews.forEach(other => {
                    if (other !== entry) clearPreview(other);
                });
                entry.previewing = true;
                element.classList.add(previewClass);
                activePreviews.add(entry);
                return;
            }

            if (entry.previewing || !isTouch) {
                activate();
            }
        }, true);
    } else {
        // Overlay mode: show overlay by default, click overlay to activate
        overlay.classList.add('is-visible');
    }

    return {
        activate,
        deactivate,
        isActivated: () => activated,
        destroy: () => {
            clearPreview(entry);
            if (overlay) overlay.remove();
        },
    };
}

export { isTouch };
